import React, { useState, useRef, useEffect } from 'react';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { ChevronDown, Check, Plus, Loader2 } from 'lucide-react';

export interface OrgSwitcherProps {
  className?: string;
}

/**
 * 100% Custom White-Label Organization Switcher for Conversora Partner Portal.
 * Zero Clerk UI elements, zero Clerk branding, zero Clerk classNames.
 * Uses Clerk's headless hook `useOrganizationList` & `useOrganization`.
 */
export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ className = '' }) => {
  const { organization: activeOrg } = useOrganization();
  const { isLoaded, setActive, userMemberships, createOrganization } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsCreating(false);
        setError(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectOrg = async (orgId: string) => {
    if (!setActive) return;
    try {
      await setActive({ organization: orgId });
      setIsOpen(false);
    } catch (err: any) {
      console.error('Failed to switch organization', err);
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createOrganization || !newOrgName.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const newOrg = await createOrganization({ name: newOrgName.trim() });
      if (setActive && newOrg) {
        await setActive({ organization: newOrg.id });
      }
      setNewOrgName('');
      setIsCreating(false);
      setIsOpen(false);
    } catch (err: any) {
      setError(err?.errors?.[0]?.message || err?.message || 'Failed to create organization.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeName = activeOrg?.name || 'My Organization';
  const orgInitials = activeName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const memberships = userMemberships?.data || [];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Custom Switcher Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-900/90 border border-zinc-800 hover:border-zinc-700 text-zinc-200 text-xs font-medium transition cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500/40 shadow-xs"
        aria-label="Switch organization"
        aria-expanded={isOpen}
      >
        {activeOrg?.imageUrl ? (
          <img
            src={activeOrg.imageUrl}
            alt={activeName}
            className="w-5 h-5 rounded object-cover"
          />
        ) : (
          <div className="w-5 h-5 rounded bg-linear-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-[10px] font-bold text-white shadow-xs">
            {orgInitials}
          </div>
        )}

        <span className="truncate max-w-[130px] font-medium text-zinc-100">
          {activeName}
        </span>

        <ChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Custom Dropdown Modal */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-[#0E1015] border border-zinc-800 rounded-2xl shadow-2xl py-2 z-50 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150">
          <div className="px-3.5 py-2 border-b border-zinc-800/80">
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
              Organizations & Agencies
            </p>
          </div>

          <div className="py-1 max-h-56 overflow-y-auto divide-y divide-zinc-850/50">
            {!isLoaded ? (
              <div className="p-4 flex items-center justify-center gap-2 text-xs text-zinc-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                <span>Loading organizations...</span>
              </div>
            ) : memberships.length === 0 ? (
              <div className="p-3 text-center text-xs text-zinc-400">
                No organizations found
              </div>
            ) : (
              memberships.map((membership) => {
                const org = membership.organization;
                const isCurrent = org.id === activeOrg?.id;
                const initials = org.name
                  .split(' ')
                  .map((w) => w[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);

                return (
                  <button
                    key={org.id}
                    type="button"
                    onClick={() => handleSelectOrg(org.id)}
                    className={`w-full px-3.5 py-2.5 text-left text-xs flex items-center justify-between transition cursor-pointer ${
                      isCurrent
                        ? 'bg-indigo-600/10 text-white font-medium'
                        : 'text-zinc-300 hover:text-white hover:bg-zinc-800/60'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {org.imageUrl ? (
                        <img
                          src={org.imageUrl}
                          alt={org.name}
                          className="w-5 h-5 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-5 h-5 rounded bg-zinc-800 border border-zinc-750 flex items-center justify-center text-[10px] font-bold text-zinc-300 shrink-0">
                          {initials}
                        </div>
                      )}
                      <span className="truncate">{org.name}</span>
                    </div>

                    {isCurrent && (
                      <Check className="w-3.5 h-3.5 text-indigo-400 shrink-0 ml-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Create Organization Footer */}
          <div className="pt-1.5 border-t border-zinc-800/80 px-2">
            {!isCreating ? (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="w-full px-3 py-2 text-left text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded-xl flex items-center gap-2 transition cursor-pointer font-medium"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create New Organization</span>
              </button>
            ) : (
              <form onSubmit={handleCreateOrg} className="p-2 space-y-2">
                <p className="text-[11px] font-semibold text-white">Create Organization</p>
                {error && (
                  <p className="text-[10px] text-rose-400">{error}</p>
                )}
                <input
                  type="text"
                  required
                  placeholder="e.g. Acme Agency"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500"
                />
                <div className="flex items-center gap-1.5 pt-1">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center justify-center gap-1"
                  >
                    {isSubmitting && <Loader2 className="w-3 h-3 animate-spin" />}
                    <span>Create</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setError(null);
                    }}
                    className="px-2.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-medium transition cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
